import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { AlveoFitLogo } from '../components/ui/Logo';

const PURPLE = '#403EC9';

// ─── Territory zones (approximate SVG paths for India) ────────────
const TERRITORIES = [
  { id:'north',     name:'North',                    cx:195, cy:105, r:22, color:'#2563EB', rep:'Piyush Kohli' },
  { id:'west',      name:'West',                     cx:130, cy:175, r:20, color:'#7C3AED', rep:'Sikandar Khan' },
  { id:'central',   name:'Central',                  cx:210, cy:180, r:18, color:'#D97706', rep:'Central Rep' },
  { id:'maha',      name:'Maharashtra',              cx:155, cy:230, r:20, color:'#403EC9', rep:'Vaibhav Bhavsar' },
  { id:'southeast', name:'South-East',               cx:240, cy:280, r:18, color:'#DC2626', rep:'SE Rep' },
  { id:'southwest', name:'South-West',               cx:165, cy:295, r:18, color:'#16A34A', rep:'SW Rep' },
];

// ─── Rotating insights ────────────────────────────────────────────
const DEFAULT_INSIGHTS = [
  { text:'2,739 doctors across 6 territories', icon:'👥' },
  { text:'Priority A coverage tracked monthly', icon:'⭐' },
  { text:'₹13.3Cr FY target across all territories', icon:'🎯' },
  { text:'Real-time beat plan + VP approval workflow', icon:'📅' },
  { text:'FOT consumable tracking built-in', icon:'🔬' },
];

export default function LoginPage() {
  const { login } = useAuth();
  const navigate   = useNavigate();
  const [form, setForm]         = useState({ email:'', password:'' });
  const [loading, setLoading]   = useState(false);
  const [showPwd, setShowPwd]   = useState(false);
  const [stats, setStats]       = useState({ doctors:'2,739', territories:'6', fy_target:'₹13.3Cr' });
  const [insights, setInsights] = useState(DEFAULT_INSIGHTS);
  const [insightIdx, setIdx]    = useState(0);
  const [snapshot, setSnapshot] = useState(null);
  const [hoveredTerritory, setHovered] = useState(null);
  const [pulseActive, setPulse] = useState(true);
  const intervalRef = useRef(null);

  // Icon map for server-returned icon codes
  const ICON_MAP = { '2739':'👥', star:'⭐', visits:'📋', demo:'🔬', target:'🎯', plan:'📅', alert:'🔔' };

  // Fetch live stats + insights
  useEffect(() => {
    fetch('/api/v1/auth/stats')
      .then(r=>r.json())
      .then(d=>{
        if(d.success) {
          setStats(d.data);
          if(d.data.insights && d.data.insights.length > 0) {
            setInsights(d.data.insights.map(i=>({ text:i.text, icon: ICON_MAP[i.icon] || '📊' })));
          }
        }
      })
      .catch(()=>{});
  }, []);

  // Rotate insights every 4 seconds
  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setIdx(i => (i+1) % insights.length);
    }, 4000);
    return () => clearInterval(intervalRef.current);
  }, [insights.length]);

  // Pulse animation toggle
  useEffect(() => {
    const t = setInterval(() => setPulse(p=>!p), 1500);
    return () => clearInterval(t);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Try to capture location silently — don't block login if denied
      let geo = {};
      try {
        const pos = await new Promise((res, rej) =>
          navigator.geolocation?.getCurrentPosition(res, rej, { timeout: 5000, maximumAge: 60000 })
        );
        geo = {
          geo_lat: parseFloat(pos.coords.latitude.toFixed(7)),
          geo_lng: parseFloat(pos.coords.longitude.toFixed(7)),
        };
      } catch {
        // Location denied or unavailable — login still proceeds
      }
      const device_type = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) ? 'mobile' : 'web';
      const user = await login(form.email, form.password, { ...geo, device_type });
      toast.success(`Welcome back, ${user.name.split(' ')[0]}!`);
      const role = user.role_slug || user.role || '';
      if (role === 'dispatch') navigate('/dispatch-dashboard');
      else if (role === 'employee') navigate('/home');
      else if (role === 'distributor') navigate('/distributor-orders');
      else if (role === 'investor') navigate('/dashboard');
      else navigate('/');
    } catch(err) {
      toast.error(err.response?.data?.message || 'Login failed');
    } finally { setLoading(false); }
  };

  const currentInsight = insights[insightIdx];

  return (
    <div className="login-page">

      {/* ── Left panel ── */}
      <div className="login-left" style={{ display:'flex', flexDirection:'column', justifyContent:'space-between', padding:'40px 44px' }}>

        {/* Logo */}
        <AlveoFitLogo variant="light" height={36} />

        {/* Center content */}
        <div style={{ textAlign:'center' }}>

          {/* India territory map */}
          <div style={{ position:'relative', display:'inline-block', marginBottom:24 }}>
            <svg width="300" height="360" viewBox="0 0 300 380" style={{ overflow:'visible' }}>
              {/* India outline — simplified path */}
              <path d="M160,20 L195,22 L225,35 L248,55 L260,80 L265,110
                       L258,140 L252,165 L260,185 L255,205 L240,220
                       L235,245 L242,268 L238,290 L225,310 L210,330
                       L195,345 L185,358 L175,350 L165,332 L155,315
                       L145,298 L138,275 L142,255 L148,235 L140,215
                       L128,198 L118,178 L112,155 L108,130 L112,105
                       L118,82 L130,60 L145,38 Z"
                fill="#1E1B4B" stroke="rgba(255,255,255,0.15)" strokeWidth="1.5"/>

              {/* Territory dots with pulse */}
              {TERRITORIES.map(t => (
                <g key={t.id}
                  onMouseEnter={()=>setHovered(t)}
                  onMouseLeave={()=>setHovered(null)}
                  style={{ cursor:'pointer' }}>
                  {/* Pulse ring */}
                  <circle cx={t.cx} cy={t.cy} r={pulseActive?t.r*1.5:t.r*1.2}
                    fill={t.color} opacity={pulseActive?0.15:0.08}
                    style={{ transition:'r 1.5s ease, opacity 1.5s ease' }}/>
                  {/* Main dot */}
                  <circle cx={t.cx} cy={t.cy} r={t.r*0.55}
                    fill={t.color} opacity={0.9}/>
                  {/* Label */}
                  <text x={t.cx} y={t.cy+t.r*0.55+14}
                    textAnchor="middle" fill="rgba(255,255,255,0.7)"
                    fontSize="9" fontFamily="system-ui">
                    {t.name}
                  </text>
                </g>
              ))}

              {/* Hover tooltip */}
              {hoveredTerritory && (
                <g>
                  <rect x={hoveredTerritory.cx-45} y={hoveredTerritory.cy-38} width={90} height={26} rx="5"
                    fill="rgba(255,255,255,0.95)"/>
                  <text x={hoveredTerritory.cx} y={hoveredTerritory.cy-22}
                    textAnchor="middle" fill="#26215C" fontSize="10" fontWeight="600" fontFamily="system-ui">
                    {hoveredTerritory.rep}
                  </text>
                </g>
              )}
            </svg>

            {/* Territory legend */}
            <div style={{ display:'flex', flexWrap:'wrap', gap:'6px 12px', justifyContent:'center', marginTop:4 }}>
              {TERRITORIES.map(t=>(
                <div key={t.id} style={{ display:'flex', alignItems:'center', gap:4, fontSize:'0.68rem', color:'rgba(255,255,255,0.6)' }}>
                  <div style={{ width:7, height:7, borderRadius:'50%', background:t.color }}/>
                  {t.name}
                </div>
              ))}
            </div>
          </div>

          {/* Rotating insight */}
          <div style={{ minHeight:52, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', marginBottom:20 }}>
            <div style={{ fontSize:'1.3rem', marginBottom:6 }}>{currentInsight.icon}</div>
            <div style={{ fontSize:'0.82rem', color:'rgba(255,255,255,0.75)', lineHeight:1.6, maxWidth:260, textAlign:'center',
              transition:'opacity 0.4s', opacity:1 }}>
              {currentInsight.text}
            </div>
            {/* Dots indicator */}
            <div style={{ display:'flex', gap:5, marginTop:10 }}>
              {insights.map((_,i)=>(
                <div key={i} onClick={()=>setIdx(i)} style={{ cursor:'pointer',
                  width: i===insightIdx ? 16 : 5, height:5, borderRadius:99,
                  background: i===insightIdx ? '#fff' : 'rgba(255,255,255,0.3)',
                  transition:'width 0.3s' }}/>
              ))}
            </div>
          </div>

          {/* Live stats */}
          <div style={{ display:'flex', gap:0, justifyContent:'center', border:'1px solid rgba(255,255,255,0.12)', borderRadius:12, overflow:'hidden' }}>
            {[
              { n:stats.doctors,     l:'Doctors' },
              { n:stats.territories, l:'Territories' },
              { n:stats.fy_target,   l:'FY Target' },
            ].map((s,i)=>(
              <div key={s.l} style={{ flex:1, textAlign:'center', padding:'12px 8px',
                borderRight: i<2 ? '1px solid rgba(255,255,255,0.12)' : 'none' }}>
                <div style={{ fontSize:'1.25rem', fontWeight:700, color:'#fff' }}>{s.n}</div>
                <div style={{ fontSize:'0.62rem', color:'rgba(255,255,255,0.45)', textTransform:'uppercase', letterSpacing:'0.08em', marginTop:2 }}>{s.l}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Today snapshot at bottom */}
        <div style={{ fontSize:'0.72rem', color:'rgba(255,255,255,0.4)', textAlign:'center', lineHeight:1.6 }}>
          Sales Intelligence Platform · Alveofit India
        </div>
      </div>

      {/* ── Right panel ── */}
      <div className="login-right">
        <div className="login-box">

          {/* Header */}
          <div style={{ marginBottom:28 }}>
            <h1 style={{ fontSize:'1.55rem', fontWeight:700, marginBottom:6, color:'var(--text-primary)' }}>Sign in</h1>
            <p style={{ color:'var(--text-secondary)', fontSize:'0.88rem' }}>
              Access your sales intelligence dashboard
            </p>
          </div>

          <form onSubmit={handleSubmit}>
            {/* Email */}
            <div className="form-group">
              <label className="form-label">Email address</label>
              <input type="email" className="form-control"
                placeholder="you@alveofit.com"
                value={form.email}
                onChange={e=>setForm({...form, email:e.target.value})}
                required autoFocus
                style={{ fontSize:'0.9rem' }}/>
            </div>

            {/* Password with show/hide */}
            <div className="form-group" style={{ marginBottom:22 }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
                <label className="form-label" style={{ marginBottom:0 }}>Password</label>
                <Link to="/forgot-password" style={{ fontSize:'0.78rem', color:PURPLE, textDecoration:'none', fontWeight:500 }}>
                  Forgot password?
                </Link>
              </div>
              <div style={{ position:'relative' }}>
                <input type={showPwd?'text':'password'} className="form-control"
                  placeholder="••••••••"
                  value={form.password}
                  onChange={e=>setForm({...form, password:e.target.value})}
                  required
                  style={{ paddingRight:44, fontSize:'0.9rem' }}/>
                <button type="button" onClick={()=>setShowPwd(s=>!s)}
                  style={{ position:'absolute', right:12, top:'50%', transform:'translateY(-50%)',
                    background:'none', border:'none', cursor:'pointer', padding:4,
                    color:'var(--text-muted)', fontSize:15, display:'flex', alignItems:'center' }}>
                  {showPwd ? (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94"/>
                      <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19"/>
                      <line x1="1" y1="1" x2="23" y2="23"/>
                    </svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {/* Sign in button */}
            <button type="submit" className="btn btn-primary btn-lg"
              style={{ width:'100%', justifyContent:'center', fontSize:'0.95rem', padding:'12px' }}
              disabled={loading}>
              {loading
                ? <><span className="spinner" style={{ width:16, height:16 }}/> Signing in…</>
                : 'Sign in →'
              }
            </button>
          </form>

        </div>
      </div>
    </div>
  );
}
